/**
 * ClientWelcome — first-time client landing for the web PWA.
 *
 * Shown to a client with no active transaction yet (no dossier). It answers
 * "what is this and how do I start?" with a value-prop hero, a preview
 * concierge chat, and a three-step "how it works".
 *
 * Every claim on this page must be something the product actually does today.
 * No ratings, client counts or closing volumes: nothing records them, and a
 * brokerage's advertising has to be supportable under TREC rules. The chat is
 * a keyword-matched preview (see `shell/chat`), so it is labelled as one and
 * never described as live or always-on.
 *
 * The chat offers no listing cards: listing search is not built, so any card
 * would be invented (spec 024 removed the last one, its page and its
 * `?listing=` deep link).
 *
 * Chat state lives in `shell/chat` so the persistent ChatDock that follows
 * the client across other pages shares the same thread. Phase 0 persists to
 * localStorage keyed by an anonymous device id (see `shell/identity`).
 * Phase 3 wires it to the voice-agent worker over voice-sdk and migrates
 * the local thread into DynamoDB keyed by `(tenant_id, client_id)`.
 *
 * Styling lives in ClientWelcome.css — self-contained, `.cw-`-prefixed.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import {
  CalendarCheck,
  FolderOpen,
  Landmark,
  MessageSquare,
  PenLine,
  Sparkles,
} from "lucide-react";

import { PublicFooter } from "../../shell/PublicFooter";
import { QUICK_REPLIES, useChat, type ChatMessage } from "../../shell/chat";

import "./ClientWelcome.css";

interface Feature {
  icon: typeof MessageSquare;
  title: string;
  body: string;
}

const FEATURES: readonly Feature[] = [
  {
    icon: FolderOpen,
    title: "Paperwork, sorted for you",
    body: "We label & check your documents.",
  },
  {
    icon: PenLine,
    title: "Sign in a few taps",
    body: "Review & e-sign TREC contracts.",
  },
  {
    icon: CalendarCheck,
    title: "Book showings",
    body: "Pick a time; your agent confirms.",
  },
  {
    icon: Landmark,
    title: "Get pre-approved",
    body: "Apply online; your loan officer reviews it.",
  },
];

interface Step {
  title: string;
  body: string;
}

const STEPS: readonly Step[] = [
  {
    title: "Create your account",
    body: "Enter your email, open the link we send you, then use Face ID or your fingerprint. There is no password to remember.",
  },
  {
    title: "Upload your documents",
    body: "Snap a photo of your ID or a pay stub. We read it, file it, and show you what is still missing.",
  },
  {
    title: "Sign or apply",
    body: "Buying? E-sign your buyer representation agreement, then request showings. Need a mortgage? Complete your loan application and your loan officer takes it from there.",
  },
];

export function ClientWelcome() {
  const { messages, typing, send } = useChat();
  const [draft, setDraft] = useState("");
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing]);

  function focusChat() {
    inputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    inputRef.current?.focus();
  }

  const canSend = draft.trim().length > 0;

  function handleSend() {
    if (!canSend) return;
    send(draft);
    setDraft("");
  }

  return (
    <div className="cw-page">
      {/* ---- hero ---- */}
      <section className="cw-hero">
        <div className="cw-blob cw-blob-1" />
        <div className="cw-blob cw-blob-2" />
        <div className="cw-blob cw-blob-3" />

        <div className="cw-hero__main">
          <div className="cw-fade cw-d1">
            <span className="cw-eyebrow">
              <Sparkles size={14} strokeWidth={2.4} />
              Your home journey, in one place
            </span>
            <h1 className="cw-h1">
              Finding home,
              <br />
              made <span className="cw-grad-text">simple.</span>
            </h1>
            <p className="cw-sub">
              Create your account, upload your documents, then sign your buyer
              agreement and book showings, or apply for your mortgage
              pre-approval, with your agent or loan officer alongside you.
            </p>
            <div className="cw-cta-row">
              <Link to="/sign-up" className="cw-btn-primary">
                Get started
              </Link>
              <button
                type="button"
                className="cw-btn-glass"
                onClick={focusChat}
              >
                <MessageSquare size={16} /> Ask a quick question
              </button>
            </div>
          </div>

          {/* concierge chat */}
          <div className="cw-chat cw-fade cw-d2">
            <div className="cw-chat-head">
              <div className="cw-avatar" aria-hidden="true">
                <MessageSquare size={20} color="white" />
              </div>
              <div>
                <div className="cw-chat-name">Home concierge</div>
                <div className="cw-preview">
                  Preview · quick answers to common questions
                </div>
              </div>
            </div>

            <div
              className="cw-chat-body"
              ref={bodyRef}
              role="log"
              aria-live="polite"
              aria-label="Concierge chat"
            >
              {messages.map((m) => (
                <ChatTurn key={m.id} message={m} />
              ))}
              {typing && (
                <div className="cw-typing" aria-label="Assistant is typing">
                  <span />
                  <span />
                  <span />
                </div>
              )}
            </div>

            <div className="cw-chips">
              {QUICK_REPLIES.map((q) => (
                <button
                  key={q}
                  type="button"
                  className="cw-chip"
                  onClick={() => send(q)}
                >
                  {q}
                </button>
              ))}
            </div>

            <div className="cw-composer">
              <label htmlFor="cw-composer-input" className="cw-sr-only">
                Message the concierge
              </label>
              <input
                id="cw-composer-input"
                ref={inputRef}
                className="cw-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSend();
                }}
                placeholder="Type a message…"
              />
              <button
                type="button"
                className="cw-send"
                onClick={handleSend}
                disabled={!canSend}
              >
                Send
              </button>
            </div>
          </div>
        </div>

        {/* ---- in-hero value cards ---- */}
        <div className="cw-hero-cards cw-fade cw-d3">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="cw-hero-card">
                <div className="cw-hero-card__icon">
                  <Icon size={17} strokeWidth={2} />
                </div>
                <div>
                  <div className="cw-hero-card__title">{f.title}</div>
                  <div className="cw-hero-card__body">{f.body}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ---- how it works ---- */}
      <section className="cw-steps cw-fade cw-d3" aria-labelledby="cw-steps-h">
        <h2 id="cw-steps-h" className="cw-steps__h">
          How it works
        </h2>
        <ol className="cw-steps__list">
          {STEPS.map((step, i) => (
            <li key={step.title} className="cw-step">
              <span className="cw-step__num" aria-hidden="true">
                {i + 1}
              </span>
              <div>
                <div className="cw-step__title">{step.title}</div>
                <p className="cw-step__body">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ---- footer: chosen by the address (shell/PublicFooter). Here it is
           the shared one — a brokerage's address renders that brokerage's own
           page at `/` instead of this one (Home.tsx). */}
      <PublicFooter />
    </div>
  );
}

function ChatTurn({ message }: { message: ChatMessage }) {
  const isBot = message.role === "bot";
  return (
    <div className={`cw-turn ${isBot ? "cw-turn--bot" : "cw-turn--user"}`}>
      <div className={`cw-msg ${isBot ? "cw-msg-bot" : "cw-msg-user"}`}>
        {message.text}
      </div>
    </div>
  );
}
